/**
 * Comprehensive Scan Results Dashboard
 * Single pane of glass for all GitHub organization scanning activities,
 * MCP detections, compliance findings, and insights
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Download,
  Filter,
  Search,
  Calendar,
  TrendingUp,
  TrendingDown,
  Users,
  GitBranch,
  Shield,
  Target,
  BarChart3,
  PieChart,
  LineChart,
  RefreshCw,
  Settings,
  FileText,
  AlertCircle,
  Info,
  ExternalLink,
  Zap,
  Database,
  Lock
} from 'lucide-react';

const ScanResultsDashboard = ({ apiService }) => {
  // State management
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Dashboard data
  const [dashboardData, setDashboardData] = useState({
    overview: {},
    scanHistory: [],
    detections: [],
    organizations: [],
    compliance: {},
    trends: {}
  });

  // Filters and search
  const [filters, setFilters] = useState({
    dateRange: '30d', // 7d, 30d, 90d, all
    organization: 'all',
    status: 'all', // completed, failed, in_progress
    severity: 'all', // critical, high, medium, low
    detectionType: 'all',
    complianceStatus: 'all'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');

  // Auto-refresh
  const intervalRef = useRef();
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();

    // Set up auto-refresh every 2 minutes
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        refreshDashboardData();
      }, 120000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh]);

  // Reload when filters change
  useEffect(() => {
    setCurrentPage(1);
    loadDashboardData();
  }, [filters, searchQuery, sortBy]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load all data in parallel
      const [
        overviewData,
        scanHistoryData,
        organizationsData,
        notificationStats
      ] = await Promise.all([
        loadOverviewData(),
        loadScanHistory(),
        loadOrganizations(),
        loadNotificationStats()
      ]);

      setDashboardData({
        overview: overviewData,
        scanHistory: scanHistoryData,
        detections: extractDetections(scanHistoryData),
        organizations: organizationsData,
        compliance: calculateComplianceMetrics(scanHistoryData),
        trends: calculateTrends(scanHistoryData),
        notifications: notificationStats
      });

    } catch (err) {
      setError('Failed to load dashboard data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshDashboardData = async () => {
    try {
      setRefreshing(true);
      await loadDashboardData();
    } catch (err) {
      console.error('Auto-refresh failed:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const loadOverviewData = async () => {
    try {
      // Get recent scan jobs and GitHub scan results
      const [scanJobs, gitHubJobs] = await Promise.all([
        apiService.listJobs(),
        apiService.getGitHubScanJobs()
      ]);

      const allJobs = [...scanJobs.jobs, ...gitHubJobs.jobs];
      const last30Days = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const recentJobs = allJobs.filter(job => new Date(job.created_at) >= last30Days);

      const overview = {
        totalScans: allJobs.length,
        recentScans: recentJobs.length,
        activeScans: allJobs.filter(job => job.status === 'running').length,
        failedScans: recentJobs.filter(job => job.status === 'failed').length,
        successRate: recentJobs.length > 0 ?
          Math.round((recentJobs.filter(job => job.status === 'completed').length / recentJobs.length) * 100) : 0,
        avgScanTime: calculateAverageScanTime(recentJobs),
        totalDetections: 0,
        criticalFindings: 0,
        organizationsScanned: new Set(allJobs.map(job => job.organization).filter(Boolean)).size
      };

      return overview;
    } catch (err) {
      console.error('Failed to load overview:', err);
      return {};
    }
  };

  const loadScanHistory = async () => {
    try {
      // Get all GitHub organizations from database and their scan results
      const orgsResponse = await apiService.getGithubOrganizations();
      const organizations = orgsResponse.organizations || [];
      const allResults = [];

      for (const org of organizations) {
        try {
          const results = await apiService.getGitHubScanResults(org.name, 50);
          if (results.scans && results.scans.length > 0) {
            allResults.push(...results.scans);
          }
        } catch (err) {
          console.error(`Failed to load results for ${org.name}:`, err);
        }
      }

      return allResults;
    } catch (err) {
      console.error('Failed to load scan history:', err);
      return [];
    }
  };

  const loadOrganizations = async () => {
    try {
      // Get GitHub organizations from database
      const orgsResponse = await apiService.getGithubOrganizations();
      const organizations = orgsResponse.organizations || [];
      const orgData = [];

      for (const org of organizations) {
        try {
          const results = await apiService.getGitHubScanResults(org.name, 1);
          const latestScan = results.scans?.[0];

          orgData.push({
            name: org.name,
            id: org.id,
            frequency: org.scan_frequency || 'manual',
            lastScan: latestScan?.started_at || org.last_scan_at || null,
            nextScan: null, // Could calculate based on frequency
            status: latestScan?.scan_status || 'pending',
            repositories: latestScan?.repositories_scanned || 0,
            detections: latestScan?.detections_count || 0,
            compliance: latestScan?.overall_compliance_score ?? null,
            scanCount: org.scan_count || 0,
            isActive: org.is_active,
            config: org
          });
        } catch (err) {
          // Even if scan results fail, still show the organization
          orgData.push({
            name: org.name,
            id: org.id,
            frequency: org.scan_frequency || 'manual',
            lastScan: org.last_scan_at || null,
            nextScan: null,
            status: 'no_scans',
            repositories: 0,
            detections: 0,
            compliance: null,
            scanCount: org.scan_count || 0,
            isActive: org.is_active,
            config: org
          });
          console.error(`Failed to load scan data for ${org.name}:`, err);
        }
      }

      return orgData;
    } catch (err) {
      console.error('Failed to load organizations:', err);
      return [];
    }
  };

  const loadNotificationStats = async () => {
    try {
      const stats = await apiService.getNotificationStats();
      return stats;
    } catch (err) {
      console.error('Failed to load notification stats:', err);
      return {};
    }
  };

  const extractDetections = (scanHistory) => {
    const detections = [];

    scanHistory.forEach(scan => {
      // Handle both 'detections' and 'mcp_detections' field names
      const scanDetections = scan.detections || scan.mcp_detections || [];
      if (scanDetections && Array.isArray(scanDetections)) {
        scanDetections.forEach(detection => {
          detections.push({
            ...detection,
            scanId: scan.scan_id,
            organization: scan.organization,
            scanDate: scan.started_at,
            // Use 'confidence' field (API field name) or 'confidence_score'
            severity: calculateSeverity(detection.confidence || detection.confidence_score || 0),
            detection_type: detection.type || detection.detection_type || 'unknown'
          });
        });
      }
    });

    return detections;
  };

  const calculateComplianceMetrics = (scanHistory) => {
    const metrics = {
      totalFindings: 0,
      compliant: 0,
      partiallyCompliant: 0,
      nonCompliant: 0,
      averageScore: 0,
      trending: 'stable',
      controlCategories: {}
    };

    // Map API type names to display names
    const categoryDisplayNames = {
      'ai_policy': 'AI Policy Docs',
      'risk_management': 'Risk Management',
      'data_governance': 'Data Governance',
      'model_lifecycle': 'Model Lifecycle',
      'testing_validation': 'Testing & Validation',
      'monitoring_auditing': 'Monitoring & Auditing',
      'documentation': 'Documentation',
      'security_controls': 'Security Controls',
      'third_party': 'Third-Party Management'
    };

    // Initialize control categories
    Object.values(categoryDisplayNames).forEach(name => {
      metrics.controlCategories[name] = { total: 0, compliant: 0, partial: 0, nonCompliant: 0 };
    });

    // Filter for scans that have a valid compliance score (including 0)
    const validScans = scanHistory.filter(scan =>
      scan.overall_compliance_score != null &&
      typeof scan.overall_compliance_score === 'number' &&
      !isNaN(scan.overall_compliance_score)
    );

    // Also process scans that have compliance_findings even if overall_compliance_score is missing
    const scansWithFindings = scanHistory.filter(scan =>
      scan.compliance_findings &&
      Array.isArray(scan.compliance_findings) &&
      scan.compliance_findings.length > 0
    );

    // Calculate average score from valid scans
    if (validScans.length > 0) {
      metrics.averageScore = Math.round(
        validScans.reduce((sum, scan) => sum + (scan.overall_compliance_score || 0), 0) / validScans.length
      );

      // Calculate trending
      if (validScans.length >= 2) {
        const recent = validScans.slice(0, Math.ceil(validScans.length / 2));
        const older = validScans.slice(Math.ceil(validScans.length / 2));

        const recentAvg = recent.reduce((sum, s) => sum + (s.overall_compliance_score || 0), 0) / recent.length;
        const olderAvg = older.reduce((sum, s) => sum + (s.overall_compliance_score || 0), 0) / older.length;

        if (recentAvg > olderAvg + 5) metrics.trending = 'improving';
        else if (recentAvg < olderAvg - 5) metrics.trending = 'declining';
      }
    }

    // Count compliance findings from all scans that have them
    scansWithFindings.forEach(scan => {
      metrics.totalFindings += scan.compliance_findings.length;

      scan.compliance_findings.forEach(finding => {
        const status = (finding.compliance_status || '').toLowerCase().replace(/-/g, '_');
        const findingType = (finding.type || '').toLowerCase().replace(/-/g, '_');

        // Count by status
        if (status === 'compliant') metrics.compliant++;
        else if (status === 'partially_compliant' || status === 'partial') metrics.partiallyCompliant++;
        else if (status === 'non_compliant' || status === 'noncompliant') metrics.nonCompliant++;
        else metrics.nonCompliant++;

        // Count by control category
        const categoryName = categoryDisplayNames[findingType] || 'Other';
        if (!metrics.controlCategories[categoryName]) {
          metrics.controlCategories[categoryName] = { total: 0, compliant: 0, partial: 0, nonCompliant: 0 };
        }
        metrics.controlCategories[categoryName].total++;
        if (status === 'compliant') metrics.controlCategories[categoryName].compliant++;
        else if (status === 'partially_compliant' || status === 'partial') metrics.controlCategories[categoryName].partial++;
        else metrics.controlCategories[categoryName].nonCompliant++;
      });
    });

    return metrics;
  };

  const calculateTrends = (scanHistory) => {
    const last30Days = scanHistory.filter(scan => {
      const scanDate = new Date(scan.started_at);
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      return scanDate >= thirtyDaysAgo;
    });

    // Group by day
    const dailyData = {};
    last30Days.forEach(scan => {
      const day = new Date(scan.started_at).toISOString().split('T')[0];
      if (!dailyData[day]) {
        dailyData[day] = { scans: 0, detections: 0 };
      }
      dailyData[day].scans++;
      dailyData[day].detections += scan.detections_count || 0;
    });

    return {
      daily: dailyData,
      totalScansLast30Days: last30Days.length,
      totalDetectionsLast30Days: last30Days.reduce((sum, s) => sum + (s.detections_count || 0), 0),
      averageDetectionsPerScan: last30Days.length > 0 ?
        Math.round(last30Days.reduce((sum, s) => sum + (s.detections_count || 0), 0) / last30Days.length) : 0
    };
  };

  const calculateAverageScanTime = (scans) => {
    const completedScans = scans.filter(scan =>
      scan.status === 'completed' && scan.started_at && scan.completed_at
    );

    if (completedScans.length === 0) return 'N/A';

    const totalTime = completedScans.reduce((sum, scan) => {
      const start = new Date(scan.started_at);
      const end = new Date(scan.completed_at);
      return sum + (end - start);
    }, 0);

    const avgMs = totalTime / completedScans.length;
    const avgMinutes = Math.round(avgMs / (1000 * 60));

    return `${avgMinutes}m`;
  };

  const calculateSeverity = (confidence) => {
    if (confidence >= 0.9) return 'critical';
    if (confidence >= 0.7) return 'high';
    if (confidence >= 0.5) return 'medium';
    return 'low';
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'N/A';
    }
  };

  const formatRelativeTime = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      if (diffDays > 0) return `${diffDays}d ago`;
      if (diffHours > 0) return `${diffHours}h ago`;
      if (diffMinutes > 0) return `${diffMinutes}m ago`;
      return 'Just now';
    } catch {
      return 'N/A';
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: '#dc2626',
      high: '#ea580c',
      medium: '#d97706',
      low: '#65a30d'
    };
    return colors[severity] || '#6b7280';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle size={16} style={{ color: '#16a34a' }} />;
      case 'failed': return <XCircle size={16} style={{ color: '#dc2626' }} />;
      case 'in_progress': case 'running': return <Clock size={16} style={{ color: '#2563eb' }} />;
      default: return <Clock size={16} style={{ color: '#6b7280' }} />;
    }
  };

  const filteredData = () => {
    let filtered = [...dashboardData.scanHistory];

    // Apply filters
    if (filters.organization !== 'all') {
      filtered = filtered.filter(scan => scan.organization === filters.organization);
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter(scan => scan.scan_status === filters.status);
    }

    if (filters.dateRange !== 'all') {
      const days = parseInt(filters.dateRange.replace('d', ''));
      const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(scan => new Date(scan.started_at) >= cutoff);
    }

    // Apply search
    if (searchQuery) {
      filtered = filtered.filter(scan =>
        scan.organization?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        scan.scan_id?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc': return new Date(b.started_at) - new Date(a.started_at);
        case 'date_asc': return new Date(a.started_at) - new Date(b.started_at);
        case 'detections_desc': return (b.detections_count || 0) - (a.detections_count || 0);
        case 'organization': return (a.organization || '').localeCompare(b.organization || '');
        default: return 0;
      }
    });

    return filtered;
  };

  const paginatedData = () => {
    const filtered = filteredData();
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filtered.slice(startIndex, startIndex + itemsPerPage);
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading comprehensive dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="scan-results-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="dashboard-title">
          <BarChart3 size={32} />
          <div>
            <h1>Scan Results Dashboard</h1>
            <p>Comprehensive view of GitHub organization scanning activities and findings</p>
          </div>
        </div>

        <div className="dashboard-actions">
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>

          <button
            className="btn-secondary"
            onClick={refreshDashboardData}
            disabled={refreshing}
          >
            <RefreshCw size={16} className={refreshing ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="dashboard-tabs">
        {[
          { id: 'overview', label: '📊 Overview', icon: BarChart3 },
          { id: 'scans', label: '🔍 Scan History', icon: Activity },
          { id: 'detections', label: '⚠️ Detections', icon: AlertTriangle },
          { id: 'organizations', label: '🏢 Organizations', icon: Users },
          { id: 'compliance', label: '🛡️ Compliance', icon: Shield },
          { id: 'trends', label: '📈 Trends', icon: TrendingUp }
        ].map(tab => (
          <button
            key={tab.id}
            className={`dashboard-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon size={20} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="dashboard-overview">
          {/* Key Metrics Cards */}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <Activity size={24} />
              </div>
              <div className="metric-content">
                <div className="metric-value">{dashboardData.overview.totalScans || 0}</div>
                <div className="metric-label">Total Scans</div>
                <div className="metric-change">
                  +{dashboardData.overview.recentScans || 0} this month
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <AlertTriangle size={24} />
              </div>
              <div className="metric-content">
                <div className="metric-value">{dashboardData.trends?.totalDetectionsLast30Days || 0}</div>
                <div className="metric-label">MCP Detections</div>
                <div className="metric-change">
                  Avg: {dashboardData.trends?.averageDetectionsPerScan || 0} per scan
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
                <Users size={24} />
              </div>
              <div className="metric-content">
                <div className="metric-value">{dashboardData.overview.organizationsScanned || 0}</div>
                <div className="metric-label">Organizations</div>
                <div className="metric-change">
                  {dashboardData.overview.activeScans || 0} active scans
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon" style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
                <CheckCircle size={24} />
              </div>
              <div className="metric-content">
                <div className="metric-value">{dashboardData.overview.successRate || 0}%</div>
                <div className="metric-label">Success Rate</div>
                <div className="metric-change">
                  Avg time: {dashboardData.overview.avgScanTime || 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="overview-sections">
            <div className="overview-section">
              <h3>
                <Clock size={20} />
                Recent Activity
              </h3>
              <div className="activity-list">
                {dashboardData.scanHistory.slice(0, 10).map((scan, index) => (
                  <div key={index} className="activity-item">
                    <div className="activity-icon">
                      {getStatusIcon(scan.scan_status)}
                    </div>
                    <div className="activity-content">
                      <div className="activity-title">
                        Scan of <strong>{scan.organization}</strong>
                      </div>
                      <div className="activity-meta">
                        {formatRelativeTime(scan.started_at)} •{' '}
                        {scan.repositories_scanned} repos •{' '}
                        {scan.detections_count || 0} detections
                      </div>
                    </div>
                    <div className="activity-status">
                      <span className={`status-badge status-${scan.scan_status}`}>
                        {scan.scan_status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="overview-section">
              <h3>
                <AlertTriangle size={20} />
                Critical Findings
              </h3>
              <div className="findings-list">
                {dashboardData.detections
                  .filter(d => d.severity === 'critical')
                  .slice(0, 5)
                  .map((detection, index) => (
                    <div key={index} className="finding-item">
                      <div className="finding-severity critical">
                        <AlertTriangle size={16} />
                      </div>
                      <div className="finding-content">
                        <div className="finding-title">{detection.detection_type}</div>
                        <div className="finding-location">
                          {detection.organization} • {detection.file_path}
                        </div>
                      </div>
                      <div className="finding-confidence">
                        {Math.round(detection.confidence_score * 100)}%
                      </div>
                    </div>
                  ))}
                {dashboardData.detections.filter(d => d.severity === 'critical').length === 0 && (
                  <div className="empty-findings">
                    <CheckCircle size={24} style={{ color: '#16a34a' }} />
                    <span>No critical findings detected</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Compliance Overview */}
          <div className="compliance-overview">
            <h3>
              <Shield size={20} />
              ISO 42001 Compliance Overview
            </h3>
            <div className="compliance-metrics">
              <div className="compliance-score">
                <div className="score-circle">
                  <div className="score-value">{dashboardData.compliance.averageScore || 0}</div>
                  <div className="score-label">Score</div>
                </div>
                <div className="score-trend">
                  {dashboardData.compliance.trending === 'improving' && (
                    <span className="trend improving">
                      <TrendingUp size={16} />
                      Improving
                    </span>
                  )}
                  {dashboardData.compliance.trending === 'declining' && (
                    <span className="trend declining">
                      <TrendingDown size={16} />
                      Declining
                    </span>
                  )}
                  {dashboardData.compliance.trending === 'stable' && (
                    <span className="trend stable">
                      <Activity size={16} />
                      Stable
                    </span>
                  )}
                </div>
              </div>

              <div className="compliance-breakdown">
                <div className="breakdown-item">
                  <div className="breakdown-value">{dashboardData.compliance.compliant || 0}</div>
                  <div className="breakdown-label">Compliant</div>
                  <div className="breakdown-bar compliant">
                    <div
                      className="breakdown-fill"
                      style={{
                        width: `${dashboardData.compliance.totalFindings > 0 ?
                          (dashboardData.compliance.compliant / dashboardData.compliance.totalFindings) * 100 : 0}%`
                      }}
                    ></div>
                  </div>
                </div>

                <div className="breakdown-item">
                  <div className="breakdown-value">{dashboardData.compliance.partiallyCompliant || 0}</div>
                  <div className="breakdown-label">Partial</div>
                  <div className="breakdown-bar partial">
                    <div
                      className="breakdown-fill"
                      style={{
                        width: `${dashboardData.compliance.totalFindings > 0 ?
                          (dashboardData.compliance.partiallyCompliant / dashboardData.compliance.totalFindings) * 100 : 0}%`
                      }}
                    ></div>
                  </div>
                </div>

                <div className="breakdown-item">
                  <div className="breakdown-value">{dashboardData.compliance.nonCompliant || 0}</div>
                  <div className="breakdown-label">Non-Compliant</div>
                  <div className="breakdown-bar non-compliant">
                    <div
                      className="breakdown-fill"
                      style={{
                        width: `${dashboardData.compliance.totalFindings > 0 ?
                          (dashboardData.compliance.nonCompliant / dashboardData.compliance.totalFindings) * 100 : 0}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scan History Tab */}
      {activeTab === 'scans' && (
        <div className="dashboard-scans">
          {/* Filters */}
          <div className="dashboard-filters">
            <div className="filters-row">
              <div className="search-box">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search scans..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <select
                value={filters.organization}
                onChange={(e) => setFilters(prev => ({ ...prev, organization: e.target.value }))}
              >
                <option value="all">All Organizations</option>
                {[...new Set(dashboardData.scanHistory.map(s => s.organization))].map(org => (
                  <option key={org} value={org}>{org}</option>
                ))}
              </select>

              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="all">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="in_progress">In Progress</option>
              </select>

              <select
                value={filters.dateRange}
                onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="all">All time</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="date_desc">Newest first</option>
                <option value="date_asc">Oldest first</option>
                <option value="detections_desc">Most detections</option>
                <option value="organization">Organization</option>
              </select>
            </div>
          </div>

          {/* Scan Results Table */}
          <div className="scans-table-container">
            <table className="scans-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Organization</th>
                  <th>Started</th>
                  <th>Duration</th>
                  <th>Repositories</th>
                  <th>Detections</th>
                  <th>Compliance</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData().map((scan, index) => (
                  <tr key={index}>
                    <td>
                      <div className="scan-status">
                        {getStatusIcon(scan.scan_status)}
                        <span className={`status-text status-${scan.scan_status}`}>
                          {scan.scan_status}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="organization-cell">
                        <strong>{scan.organization}</strong>
                        <small>{scan.scan_id}</small>
                      </div>
                    </td>
                    <td>
                      <div className="date-cell">
                        <div>{formatDate(scan.started_at)}</div>
                        <small>{formatRelativeTime(scan.started_at)}</small>
                      </div>
                    </td>
                    <td>
                      {scan.completed_at && scan.started_at ? (
                        (() => {
                          const duration = new Date(scan.completed_at) - new Date(scan.started_at);
                          const minutes = Math.round(duration / (1000 * 60));
                          return `${minutes}m`;
                        })()
                      ) : (
                        scan.scan_status === 'in_progress' ? 'Running...' : 'N/A'
                      )}
                    </td>
                    <td>{scan.repositories_scanned || 0}</td>
                    <td>
                      <div className="detections-cell">
                        <span className="detection-count">{scan.detections_count || 0}</span>
                        {scan.detections_count > 0 && (
                          <div className="detection-breakdown">
                            {scan.detections?.filter(d => calculateSeverity(d.confidence_score) === 'critical').length || 0} critical
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      {scan.overall_compliance_score !== null ? (
                        <div className="compliance-cell">
                          <div className="compliance-score">{scan.overall_compliance_score}%</div>
                          <div className={`compliance-status ${scan.overall_compliance_score >= 80 ? 'good' : scan.overall_compliance_score >= 60 ? 'fair' : 'poor'}`}>
                            {scan.overall_compliance_score >= 80 ? 'Good' : scan.overall_compliance_score >= 60 ? 'Fair' : 'Poor'}
                          </div>
                        </div>
                      ) : (
                        <span className="no-data">N/A</span>
                      )}
                    </td>
                    <td>
                      <div className="scan-actions">
                        <button className="action-btn" title="View Details">
                          <Eye size={14} />
                        </button>
                        <button className="action-btn" title="Download Report">
                          <Download size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <div className="pagination-info">
              Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredData().length)} of {filteredData().length} scans
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="pagination-current">
                Page {currentPage} of {Math.ceil(filteredData().length / itemsPerPage)}
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev =>
                  Math.min(Math.ceil(filteredData().length / itemsPerPage), prev + 1)
                )}
                disabled={currentPage >= Math.ceil(filteredData().length / itemsPerPage)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detections Tab */}
      {activeTab === 'detections' && (
        <div className="dashboard-detections">
          <div className="detections-header">
            <h3>MCP Detections Analysis</h3>
            <div className="detections-summary">
              Total: {dashboardData.detections.length} detections across {new Set(dashboardData.detections.map(d => d.organization)).size} organizations
            </div>
          </div>

          {/* Detection Type Breakdown */}
          <div className="detection-breakdown">
            {Object.entries(
              dashboardData.detections.reduce((acc, d) => {
                acc[d.detection_type] = (acc[d.detection_type] || 0) + 1;
                return acc;
              }, {})
            ).map(([type, count]) => (
              <div key={type} className="detection-type-card">
                <div className="detection-type-icon">
                  <Target size={20} />
                </div>
                <div className="detection-type-content">
                  <div className="detection-type-name">{type.replace(/_/g, ' ').toUpperCase()}</div>
                  <div className="detection-type-count">{count} detections</div>
                </div>
              </div>
            ))}
          </div>

          {/* Detection Details */}
          <div className="detections-list">
            {dashboardData.detections
              .sort((a, b) => new Date(b.scanDate) - new Date(a.scanDate))
              .slice(0, 50)
              .map((detection, index) => (
                <div key={index} className="detection-item">
                  <div className="detection-severity">
                    <div
                      className={`severity-indicator severity-${detection.severity}`}
                      style={{ backgroundColor: getSeverityColor(detection.severity) }}
                    >
                      <AlertTriangle size={16} />
                    </div>
                    <span className="severity-label">{detection.severity.toUpperCase()}</span>
                  </div>

                  <div className="detection-content">
                    <div className="detection-title">
                      {detection.detection_type.replace(/_/g, ' ')} detected
                    </div>
                    <div className="detection-location">
                      <strong>{detection.organization}</strong> • {detection.file_path}
                    </div>
                    <div className="detection-evidence">
                      {detection.evidence?.slice(0, 2).map((evidence, i) => (
                        <div key={i} className="evidence-item">{evidence}</div>
                      ))}
                    </div>
                  </div>

                  <div className="detection-meta">
                    <div className="detection-confidence">
                      Confidence: {Math.round(detection.confidence_score * 100)}%
                    </div>
                    <div className="detection-date">
                      {formatRelativeTime(detection.scanDate)}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Organizations Tab */}
      {activeTab === 'organizations' && (
        <div className="dashboard-organizations">
          <div className="organizations-grid">
            {dashboardData.organizations.map((org, index) => (
              <div key={index} className="organization-card">
                <div className="org-header">
                  <div className="org-name">
                    <GitBranch size={20} />
                    <h4>{org.name}</h4>
                  </div>
                  <div className={`org-status org-status-${org.status}`}>
                    {getStatusIcon(org.status)}
                    {org.status}
                  </div>
                </div>

                <div className="org-stats">
                  <div className="org-stat">
                    <span className="stat-value">{org.repositories}</span>
                    <span className="stat-label">Repositories</span>
                  </div>
                  <div className="org-stat">
                    <span className="stat-value">{org.detections}</span>
                    <span className="stat-label">Detections</span>
                  </div>
                  <div className="org-stat">
                    <span className="stat-value">{org.compliance !== null ? `${org.compliance}%` : 'N/A'}</span>
                    <span className="stat-label">Compliance</span>
                  </div>
                </div>

                <div className="org-schedule">
                  <div className="schedule-item">
                    <Clock size={14} />
                    <span>Frequency: {org.frequency}</span>
                  </div>
                  <div className="schedule-item">
                    <Calendar size={14} />
                    <span>Last scan: {org.lastScan ? formatRelativeTime(org.lastScan) : 'Never'}</span>
                  </div>
                  <div className="schedule-item">
                    <Zap size={14} />
                    <span>Next scan: {org.nextScan ? formatRelativeTime(org.nextScan) : 'Not scheduled'}</span>
                  </div>
                </div>

                <div className="org-actions">
                  <button className="btn-secondary" title="View Configuration">
                    <Settings size={16} />
                    Configure
                  </button>
                  <button className="btn-secondary" title="View Reports">
                    <FileText size={16} />
                    Reports
                  </button>
                  <button className="btn-primary" title="Scan Now">
                    <RefreshCw size={16} />
                    Scan Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Compliance Tab */}
      {activeTab === 'compliance' && (
        <div className="dashboard-compliance">
          <div className="compliance-header">
            <h3>ISO/IEC 42001 Compliance Dashboard</h3>
            <div className="compliance-summary">
              Overall Score: <strong>{dashboardData.compliance.averageScore || 0}/100</strong> •
              {dashboardData.compliance.totalFindings || 0} findings across all scans
            </div>
          </div>

          {/* Compliance Metrics */}
          <div className="compliance-metrics-detail">
            <div className="compliance-chart">
              <div className="chart-header">
                <h4>Compliance Distribution</h4>
              </div>
              <div className="chart-content">
                <div className="compliance-pie">
                  <div className="pie-segment compliant" style={{
                    '--percentage': dashboardData.compliance.totalFindings > 0 ?
                      (dashboardData.compliance.compliant / dashboardData.compliance.totalFindings) * 100 : 0
                  }}>
                    <span className="pie-label">
                      {dashboardData.compliance.compliant || 0} Compliant
                    </span>
                  </div>
                  <div className="pie-segment partial" style={{
                    '--percentage': dashboardData.compliance.totalFindings > 0 ?
                      (dashboardData.compliance.partiallyCompliant / dashboardData.compliance.totalFindings) * 100 : 0
                  }}>
                    <span className="pie-label">
                      {dashboardData.compliance.partiallyCompliant || 0} Partial
                    </span>
                  </div>
                  <div className="pie-segment non-compliant" style={{
                    '--percentage': dashboardData.compliance.totalFindings > 0 ?
                      (dashboardData.compliance.nonCompliant / dashboardData.compliance.totalFindings) * 100 : 0
                  }}>
                    <span className="pie-label">
                      {dashboardData.compliance.nonCompliant || 0} Non-Compliant
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="compliance-controls">
              <h4>Control Categories</h4>
              <div className="controls-grid">
                {Object.entries(dashboardData.compliance.controlCategories || {}).map(([category, counts]) => (
                  <div key={category} className="control-item">
                    <Lock size={16} />
                    <span>{category}</span>
                    <div className="control-counts">
                      <span className="count-total">{counts.total}</span>
                      {counts.total > 0 && (
                        <div className={`control-status ${counts.compliant === counts.total ? 'compliant' : counts.nonCompliant > 0 ? 'non-compliant' : 'partial'}`}>
                          {counts.compliant === counts.total ? (
                            <CheckCircle size={14} />
                          ) : counts.nonCompliant > 0 ? (
                            <XCircle size={14} />
                          ) : (
                            <AlertCircle size={14} />
                          )}
                        </div>
                      )}
                      {counts.total === 0 && (
                        <div className="control-status unknown">
                          <Info size={14} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Compliance Findings */}
          <div className="recent-findings">
            <h4>Recent Compliance Findings</h4>
            <div className="findings-table">
              <table>
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Control</th>
                    <th>Status</th>
                    <th>Confidence</th>
                    <th>Recommendations</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.scanHistory
                    .flatMap(scan =>
                      (scan.compliance_findings || []).map(finding => ({
                        ...finding,
                        organization: scan.organization,
                        scanDate: scan.started_at
                      }))
                    )
                    .sort((a, b) => new Date(b.scanDate) - new Date(a.scanDate))
                    .slice(0, 20)
                    .map((finding, index) => (
                      <tr key={index}>
                        <td>{finding.organization}</td>
                        <td>{finding.control_id}</td>
                        <td>
                          <span className={`compliance-status ${finding.compliance_status}`}>
                            {finding.compliance_status.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td>{Math.round(finding.confidence * 100)}%</td>
                        <td className="recommendations-cell">
                          {finding.recommendations?.slice(0, 2).join('; ')}
                        </td>
                        <td>{formatRelativeTime(finding.scanDate)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Trends Tab */}
      {activeTab === 'trends' && (
        <div className="dashboard-trends">
          <div className="trends-header">
            <h3>Scanning Trends & Analytics</h3>
            <div className="trends-period">
              Last 30 days: {dashboardData.trends?.totalScansLast30Days || 0} scans,
              {dashboardData.trends?.totalDetectionsLast30Days || 0} detections
            </div>
          </div>

          {/* Trend Charts */}
          <div className="trends-charts">
            <div className="trend-chart">
              <h4>
                <LineChart size={20} />
                Detection Trends
              </h4>
              <div className="chart-placeholder">
                <div className="chart-message">
                  <BarChart3 size={48} />
                  <p>Detection trends visualization</p>
                  <small>Shows MCP detections over time</small>
                </div>
              </div>
            </div>

            <div className="trend-chart">
              <h4>
                <PieChart size={20} />
                Detection Types
              </h4>
              <div className="detection-types-chart">
                {Object.entries(
                  dashboardData.detections.reduce((acc, d) => {
                    acc[d.detection_type] = (acc[d.detection_type] || 0) + 1;
                    return acc;
                  }, {})
                ).map(([type, count]) => (
                  <div key={type} className="type-bar">
                    <span className="type-name">{type.replace(/_/g, ' ')}</span>
                    <div className="type-bar-container">
                      <div
                        className="type-bar-fill"
                        style={{
                          width: `${(count / Math.max(...Object.values(
                            dashboardData.detections.reduce((acc, d) => {
                              acc[d.detection_type] = (acc[d.detection_type] || 0) + 1;
                              return acc;
                            }, {})
                          ))) * 100}%`
                        }}
                      ></div>
                    </div>
                    <span className="type-count">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Key Insights */}
          <div className="trends-insights">
            <h4>Key Insights</h4>
            <div className="insights-grid">
              <div className="insight-card">
                <TrendingUp size={24} style={{ color: '#16a34a' }} />
                <div>
                  <h5>Scan Volume</h5>
                  <p>{dashboardData.trends?.totalScansLast30Days || 0} scans completed in the last 30 days</p>
                </div>
              </div>
              <div className="insight-card">
                <Target size={24} style={{ color: '#dc2626' }} />
                <div>
                  <h5>Average Detections</h5>
                  <p>{dashboardData.trends?.averageDetectionsPerScan || 0} MCP detections per scan on average</p>
                </div>
              </div>
              <div className="insight-card">
                <Database size={24} style={{ color: '#2563eb' }} />
                <div>
                  <h5>Most Scanned Org</h5>
                  <p>{dashboardData.organizations.length > 0 ?
                    dashboardData.organizations.sort((a, b) => b.repositories - a.repositories)[0]?.name || 'N/A'
                    : 'N/A'}</p>
                </div>
              </div>
              <div className="insight-card">
                <Shield size={24} style={{ color: '#7c3aed' }} />
                <div>
                  <h5>Compliance Trend</h5>
                  <p>Overall compliance is {dashboardData.compliance.trending}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScanResultsDashboard;